import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getMyOrders,
  getOrderStatusConfig,
  formatDate,
  getTimeAgo,
  ORDER_STATUS,
} from "../services/api";

// Labels utilisateur-friendly pour les statuts
const USER_ORDER_STATUS_LABELS = {
  [ORDER_STATUS.EN_ATTENTE_PAIEMENT]: "En attente de paiement",
  [ORDER_STATUS.PAYEE]: "Commande confirmée",
  [ORDER_STATUS.EN_PREPARATION]: "En cours de préparation",
  [ORDER_STATUS.EXPEDIEE]: "Commande expédiée",
  [ORDER_STATUS.LIVREE]: "Commande livrée",
  [ORDER_STATUS.ANNULEE]: "Commande annulée",
  [ORDER_STATUS.RETOURNEE]: "Commande retournée",
};

// Configuration personnalisée des statuts pour l'utilisateur
const getUserOrderStatusConfig = (status) => {
  const baseConfig = getOrderStatusConfig(status);
  return {
    ...baseConfig,
    label: USER_ORDER_STATUS_LABELS[status] || baseConfig.label,
  };
};

// Styles personnalisés cohérents avec Home.js
const styles = {
  heroSection: {
    backgroundColor: "#543ba4",
    borderRadius: "0",
    padding: "2rem 0",
    marginBottom: "2rem",
  },
  sectionTitle: {
    position: "relative",
    marginBottom: "2.5rem",
    textAlign: "center",
    fontWeight: "600",
  },
  sectionTitleLine: {
    content: '""',
    display: "block",
    width: "60px",
    height: "3px",
    backgroundColor: "#543ba4",
    margin: "1rem auto",
    borderRadius: "2px",
  },
  orderCard: {
    border: "1px solid #e9ecef",
    borderRadius: "0.75rem",
    boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.08)",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    overflow: "hidden",
  },
  orderCardHover: {
    transform: "translateY(-2px)",
    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.1)",
  },
  statusBadge: {
    fontSize: "0.85rem",
    fontWeight: "500",
    padding: "0.5rem 1rem",
    borderRadius: "50rem",
  },
  orderHeader: {
    backgroundColor: "#f8f9fa",
    padding: "1.25rem",
    borderBottom: "1px solid #e9ecef",
  },
  priceTag: {
    color: "#543ba4",
    fontWeight: "700",
    fontSize: "1.25rem",
  },
  btnPrimary: {
    backgroundColor: "#543ba4",
    borderColor: "#543ba4",
    color: "white",
    fontWeight: "500",
    padding: "0.5rem 1.5rem",
    borderRadius: "0.5rem",
    transition: "all 0.3s ease",
  },
  btnOutline: {
    backgroundColor: "transparent",
    borderColor: "#543ba4",
    color: "#543ba4",
    fontWeight: "500",
    padding: "0.5rem 1.5rem",
    borderRadius: "0.5rem",
    transition: "all 0.3s ease",
  },
  filterButton: {
    backgroundColor: "transparent",
    border: "1px solid #dee2e6",
    color: "#6c757d",
    padding: "0.5rem 1rem",
    borderRadius: "0.5rem",
    transition: "all 0.3s ease",
    cursor: "pointer",
  },
  filterButtonActive: {
    backgroundColor: "#543ba4",
    borderColor: "#543ba4",
    color: "white",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem 1rem",
    color: "#6c757d",
  },
  progressBar: {
    height: "8px",
    borderRadius: "4px",
    backgroundColor: "#e9ecef",
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    backgroundColor: "#543ba4",
    transition: "width 0.3s ease",
  },
  timelineItem: {
    position: "relative",
    paddingLeft: "2rem",
    paddingBottom: "1rem",
  },
  timelineIcon: {
    position: "absolute",
    left: "0",
    top: "0.2rem",
    width: "1.5rem",
    height: "1.5rem",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
  },
  timelineLine: {
    position: "absolute",
    left: "0.75rem",
    top: "1.5rem",
    bottom: "-1rem",
    width: "2px",
    backgroundColor: "#dee2e6",
  },
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localSearch, setLocalSearch] = useState("");

  // Fonction pour récupérer les commandes
  const fetchOrders = useCallback(async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const response = await getMyOrders();
      const ordersData = Array.isArray(response.data) ? response.data : [];

      // Trier par date de création (plus récent en premier)
      const sortedOrders = ordersData.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setOrders(sortedOrders);

      if (showRefreshToast) {
        toast.success("Commandes actualisées");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des commandes:", error);
      toast.error("Impossible de charger vos commandes");
      setOrders([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Double filtrage : par catégorie puis par recherche
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Filtrage par catégorie
    if (filter === "active") {
      filtered = orders.filter((order) =>
        [
          ORDER_STATUS.EN_ATTENTE_PAIEMENT,
          ORDER_STATUS.PAYEE,
          ORDER_STATUS.EN_PREPARATION,
          ORDER_STATUS.EXPEDIEE,
        ].includes(order.orderStatus)
      );
    } else if (filter === "completed") {
      filtered = orders.filter(
        (order) => order.orderStatus === ORDER_STATUS.LIVREE
      );
    } else if (filter === "canceled") {
      filtered = orders.filter((order) =>
        [ORDER_STATUS.ANNULEE, ORDER_STATUS.RETOURNEE].includes(
          order.orderStatus
        )
      );
    }

    // Filtrage par recherche
    if (localSearch) {
      const searchLower = localSearch.toLowerCase();
      filtered = filtered.filter((order) => {
        return (
          order.orderId?.toLowerCase().includes(searchLower) ||
          order.billingInfo?.email?.toLowerCase().includes(searchLower) ||
          order.billingInfo?.firstName?.toLowerCase().includes(searchLower) ||
          order.billingInfo?.lastName?.toLowerCase().includes(searchLower) ||
          order.items?.some((item) =>
            item.name?.toLowerCase().includes(searchLower)
          ) ||
          order.tracking?.trackingNumber?.toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [filter, orders, localSearch]);

  // Compter les commandes par catégorie
  const statusCounts = useMemo(
    () => ({
      all: orders.length,
      active: orders.filter((o) =>
        [
          ORDER_STATUS.EN_ATTENTE_PAIEMENT,
          ORDER_STATUS.PAYEE,
          ORDER_STATUS.EN_PREPARATION,
          ORDER_STATUS.EXPEDIEE,
        ].includes(o.orderStatus)
      ).length,
      completed: orders.filter((o) => o.orderStatus === ORDER_STATUS.LIVREE)
        .length,
      canceled: orders.filter((o) =>
        [ORDER_STATUS.ANNULEE, ORDER_STATUS.RETOURNEE].includes(o.orderStatus)
      ).length,
    }),
    [orders]
  );

  // Fonction pour obtenir le style du badge de statut
  const getOrderStatusStyle = (orderStatus) => {
    const config = getUserOrderStatusConfig(orderStatus);
    return {
      ...styles.statusBadge,
      backgroundColor: config.bgColor,
      color: config.textColor,
    };
  };

  // Badge de statut de paiement
  const renderPaymentStatusBadge = (status) => {
    const configs = {
      paid: { label: "Payé", icon: "bi-check-circle-fill", bgClass: "success" },
      pending: { label: "En attente", icon: "bi-clock", bgClass: "warning" },
      open: { label: "Ouvert", icon: "bi-circle", bgClass: "info" },
      failed: { label: "Échoué", icon: "bi-x-circle", bgClass: "danger" },
      canceled: {
        label: "Annulé",
        icon: "bi-dash-circle",
        bgClass: "secondary",
      },
      expired: { label: "Expiré", icon: "bi-clock-history", bgClass: "dark" },
    };

    const config = configs[status] || {
      label: status,
      icon: "bi-question-circle",
      bgClass: "secondary",
    };

    return (
      <span
        className={`badge bg-${config.bgClass} d-inline-flex align-items-center`}
      >
        <i
          className={`bi ${config.icon} me-1`}
          style={{ fontSize: "0.75rem" }}
        ></i>
        {config.label}
      </span>
    );
  };

  // Fonction pour basculer l'expansion d'une commande
  const toggleOrderExpansion = useCallback((orderId) => {
    setExpandedOrder((prev) => (prev === orderId ? null : orderId));
  }, []);

  // Fonction pour générer la timeline complète
  const generateCompleteTimeline = useCallback((order) => {
    const allSteps = [
      {
        status: ORDER_STATUS.EN_ATTENTE_PAIEMENT,
        date: order.createdAt,
        label: "Commande créée",
        icon: "bi-cart-check",
        completed: true,
      },
      {
        status: ORDER_STATUS.PAYEE,
        date: order.paidAt || order.confirmedAt,
        label: "Paiement confirmé",
        icon: "bi-credit-card-2-front",
        completed: false,
      },
      {
        status: ORDER_STATUS.EN_PREPARATION,
        date: order.preparationStartedAt,
        label: "Préparation en cours",
        icon: "bi-box-seam",
        completed: false,
      },
      {
        status: ORDER_STATUS.EXPEDIEE,
        date: order.shippedAt,
        label: "Colis expédié",
        icon: "bi-truck",
        completed: false,
      },
      {
        status: ORDER_STATUS.LIVREE,
        date: order.deliveredAt,
        label: "Livraison effectuée",
        icon: "bi-house-check",
        completed: false,
      },
    ];

    // Marquer les étapes complétées
    const currentIndex = allSteps.findIndex(
      (step) => step.status === order.orderStatus
    );
    allSteps.forEach((step, index) => {
      if (index <= currentIndex) {
        step.completed = true;
      }
    });

    // Gérer les cas spéciaux (annulé/retourné)
    if (order.orderStatus === ORDER_STATUS.ANNULEE) {
      return [
        ...allSteps.slice(0, currentIndex + 1),
        {
          status: ORDER_STATUS.ANNULEE,
          date: order.canceledAt || order.updatedAt,
          label: "Commande annulée",
          icon: "bi-x-circle",
          completed: true,
          isSpecial: true,
        },
      ];
    } else if (order.orderStatus === ORDER_STATUS.RETOURNEE) {
      return [
        ...allSteps,
        {
          status: ORDER_STATUS.RETOURNEE,
          date: order.returnedAt || order.updatedAt,
          label: "Commande retournée",
          icon: "bi-arrow-return-left",
          completed: true,
          isSpecial: true,
        },
      ];
    }

    return allSteps;
  }, []);

  // Fonction pour calculer le prix total des articles
  const calculateItemsTotal = useCallback((items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce(
      (total, item) => total + item.price * (item.quantity || 1),
      0
    );
  }, []);

  // Fonction pour gérer le téléchargement de facture
  const handleDownloadInvoice = useCallback((orderId) => {
    toast.info("Le téléchargement des factures sera bientôt disponible");
    // TODO: Implémenter le téléchargement de facture
  }, []);

  // Fonction pour gérer les avis
  const handleLeaveReview = useCallback((orderId) => {
    toast.info("La fonction d'avis sera bientôt disponible");
    // TODO: Implémenter la fonction d'avis
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section style={styles.heroSection} className="text-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-12 text-center">
              <h1 className="display-5 fw-bold mb-3">Mes commandes</h1>
              <p className="lead mb-0">
                Suivez l'état de vos commandes et retrouvez votre historique
                d'achats
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Barre de recherche */}
      <section className="py-3 bg-white border-bottom">
        <div className="container">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher par N° commande, email, nom, article..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              aria-label="Rechercher dans mes commandes"
            />
            {localSearch && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setLocalSearch("")}
                aria-label="Effacer la recherche"
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Filtres et statistiques */}
      <section className="py-4 bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex flex-wrap gap-2">
                <button
                  style={{
                    ...styles.filterButton,
                    ...(filter === "all" ? styles.filterButtonActive : {}),
                  }}
                  onClick={() => setFilter("all")}
                >
                  Toutes ({statusCounts.all})
                </button>
                <button
                  style={{
                    ...styles.filterButton,
                    ...(filter === "active" ? styles.filterButtonActive : {}),
                  }}
                  onClick={() => setFilter("active")}
                >
                  <i className="bi bi-play-circle me-1"></i>
                  En cours ({statusCounts.active})
                </button>
                <button
                  style={{
                    ...styles.filterButton,
                    ...(filter === "completed"
                      ? styles.filterButtonActive
                      : {}),
                  }}
                  onClick={() => setFilter("completed")}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Livrées ({statusCounts.completed})
                </button>
                <button
                  style={{
                    ...styles.filterButton,
                    ...(filter === "canceled" ? styles.filterButtonActive : {}),
                  }}
                  onClick={() => setFilter("canceled")}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Annulées ({statusCounts.canceled})
                </button>
              </div>
            </div>
            <div className="col-md-4 text-md-end mt-3 mt-md-0">
              <button
                className="btn btn-outline-secondary"
                onClick={() => fetchOrders(true)}
                disabled={isRefreshing}
              >
                <i
                  className={`bi bi-arrow-clockwise me-1 ${
                    isRefreshing ? "spin" : ""
                  }`}
                ></i>
                Actualiser
              </button>
            </div>
          </div>

          {/* Résumé des filtres actifs */}
          {(filter !== "all" || localSearch) && (
            <div className="mt-3 text-muted small">
              {localSearch && <span>Recherche: "{localSearch}" - </span>}
              {filteredOrders.length} commande(s) trouvée(s)
            </div>
          )}
        </div>
      </section>

      {/* Liste des commandes */}
      <section className="py-5">
        <div className="container">
          {isLoading ? (
            // État de chargement
            <div className="text-center py-5">
              <div
                className="spinner-border"
                style={{ color: "#543ba4" }}
                role="status"
              >
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3">Chargement de vos commandes...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            // État vide
            <div style={styles.emptyState}>
              <i
                className="bi bi-box-seam"
                style={{
                  fontSize: "4rem",
                  color: "#dee2e6",
                  marginBottom: "1rem",
                }}
              ></i>
              <h3 className="mb-3">
                {localSearch
                  ? "Aucune commande ne correspond à votre recherche"
                  : filter === "all"
                  ? "Aucune commande"
                  : "Aucune commande dans cette catégorie"}
              </h3>
              <p className="mb-4">
                {localSearch
                  ? "Essayez avec d'autres termes de recherche."
                  : filter === "all"
                  ? "Vous n'avez pas encore passé de commande."
                  : "Aucune commande ne correspond au filtre sélectionné."}
              </p>
              {filter === "all" && !localSearch && (
                <Link to="/category" className="btn" style={styles.btnPrimary}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Découvrir nos box
                </Link>
              )}
            </div>
          ) : (
            // Liste des commandes
            <div className="row g-4">
              {filteredOrders.map((order) => {
                const statusConfig = getUserOrderStatusConfig(
                  order.orderStatus
                );
                const isExpanded = expandedOrder === order._id;

                return (
                  <div key={order._id} className="col-12">
                    <div
                      style={styles.orderCard}
                      className="order-card"
                      onMouseEnter={(e) => {
                        Object.assign(
                          e.currentTarget.style,
                          styles.orderCardHover
                        );
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "none";
                        e.currentTarget.style.boxShadow =
                          "0 0.25rem 0.75rem rgba(0, 0, 0, 0.08)";
                      }}
                    >
                      {/* En-tête de la commande */}
                      <div style={styles.orderHeader}>
                        <div className="row align-items-center">
                          <div className="col-lg-3 col-md-4">
                            <h6 className="mb-1 fw-bold">
                              Commande #{order.orderId}
                            </h6>
                            <small className="text-muted d-block">
                              {formatDate(order.createdAt, {
                                hour: undefined,
                                minute: undefined,
                              })}
                            </small>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {getTimeAgo(order.createdAt)}
                            </small>
                          </div>

                          <div className="col-lg-3 col-md-4 text-center mt-2 mt-md-0">
                            <span
                              style={getOrderStatusStyle(order.orderStatus)}
                            >
                              <i className={`${statusConfig.icon} me-1`}></i>
                              {statusConfig.label}
                            </span>
                            {/* Barre de progression */}
                            <div style={styles.progressBar} className="mt-2">
                              <div
                                style={{
                                  ...styles.progressBarFill,
                                  width: `${statusConfig.progress}%`,
                                  backgroundColor: statusConfig.color,
                                }}
                              ></div>
                            </div>
                          </div>

                          <div className="col-lg-2 col-md-4 text-center mt-2 mt-md-0">
                            <span style={styles.priceTag}>
                              {order.amount.toFixed(2)} {order.currency}
                            </span>
                            <div className="mt-1">
                              {renderPaymentStatusBadge(order.status)}
                            </div>
                          </div>

                          <div className="col-lg-2 text-center mt-2 mt-lg-0">
                            <small className="text-muted">
                              <i className="bi bi-box me-1"></i>
                              {order.items?.length || 0} article
                              {(order.items?.length || 0) > 1 ? "s" : ""}
                            </small>
                            {order.tracking?.trackingNumber && (
                              <div className="mt-1">
                                <small className="text-info">
                                  <i className="bi bi-truck me-1"></i>
                                  Suivi disponible
                                </small>
                              </div>
                            )}
                          </div>

                          <div className="col-lg-2 text-center mt-2 mt-lg-0">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => toggleOrderExpansion(order._id)}
                              aria-expanded={isExpanded}
                              aria-controls={`order-details-${order._id}`}
                            >
                              <i
                                className={`bi bi-chevron-${
                                  isExpanded ? "up" : "down"
                                }`}
                              ></i>
                              <span className="ms-1 d-none d-sm-inline">
                                {isExpanded ? "Masquer" : "Détails"}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Détails de la commande (expansible) */}
                      {isExpanded && (
                        <div
                          className="p-4 border-top"
                          id={`order-details-${order._id}`}
                        >
                          <div className="row">
                            {/* Timeline de la commande */}
                            <div className="col-md-4">
                              <h6 className="fw-bold mb-3">
                                <i className="bi bi-clock-history me-2"></i>
                                Suivi de la commande
                              </h6>
                              <div className="timeline">
                                {generateCompleteTimeline(order).map(
                                  (step, index, array) => {
                                    const stepConfig = getUserOrderStatusConfig(
                                      step.status
                                    );
                                    const isLast = index === array.length - 1;
                                    const isCurrent =
                                      step.status === order.orderStatus;

                                    return (
                                      <div
                                        key={`${step.status}-${index}`}
                                        style={styles.timelineItem}
                                      >
                                        {!isLast && (
                                          <div
                                            style={{
                                              ...styles.timelineLine,
                                              backgroundColor: step.completed
                                                ? stepConfig.color
                                                : "#dee2e6",
                                              opacity: step.completed ? 0.3 : 1,
                                            }}
                                          ></div>
                                        )}
                                        <div
                                          style={{
                                            ...styles.timelineIcon,
                                            backgroundColor: step.completed
                                              ? step.isSpecial
                                                ? stepConfig.color
                                                : isCurrent
                                                ? stepConfig.color
                                                : "#28a745"
                                              : "#fff",
                                            border: step.completed
                                              ? "none"
                                              : "2px solid #dee2e6",
                                            color: step.completed
                                              ? "white"
                                              : "#dee2e6",
                                          }}
                                        >
                                          <i
                                            className={
                                              step.icon || stepConfig.icon
                                            }
                                          ></i>
                                        </div>
                                        <div className="ms-3">
                                          <h6
                                            className={`mb-1 ${
                                              step.completed ? "" : "text-muted"
                                            } ${
                                              isCurrent ? "text-primary" : ""
                                            }`}
                                          >
                                            {step.label}
                                          </h6>
                                          {step.date && step.completed ? (
                                            <small className="text-muted">
                                              {formatDate(step.date)}
                                            </small>
                                          ) : (
                                            <small className="text-muted">
                                              {step.completed
                                                ? "Date non disponible"
                                                : "À venir"}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>

                            {/* Articles commandés */}
                            <div className="col-md-4">
                              <h6 className="fw-bold mb-3">
                                <i className="bi bi-bag me-2"></i>
                                Articles commandés
                              </h6>
                              {order.items && order.items.length > 0 ? (
                                <div className="list-group list-group-flush">
                                  {order.items.map((item, index) => (
                                    <div
                                      key={index}
                                      className="list-group-item px-0 py-2"
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                          <h6 className="mb-1">{item.name}</h6>
                                          <small className="text-muted">
                                            Quantité: {item.quantity || 1}
                                            {item.isSubscription && (
                                              <span className="badge bg-info ms-2">
                                                Abonnement
                                              </span>
                                            )}
                                          </small>
                                        </div>
                                        <span className="fw-bold">
                                          {item.price
                                            ? `${(
                                                item.price *
                                                (item.quantity || 1)
                                              ).toFixed(2)} €`
                                            : "N/A"}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                  {order.items.length > 0 && (
                                    <div className="list-group-item px-0 py-2 border-top mt-2">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0">
                                          Sous-total articles
                                        </h6>
                                        <span className="fw-bold">
                                          {calculateItemsTotal(
                                            order.items
                                          ).toFixed(2)}{" "}
                                          €
                                        </span>
                                      </div>
                                      {order.amount !==
                                        calculateItemsTotal(order.items) && (
                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                          <small className="text-muted">
                                            Frais de livraison
                                          </small>
                                          <small className="text-muted">
                                            {(
                                              order.amount -
                                              calculateItemsTotal(order.items)
                                            ).toFixed(2)}{" "}
                                            €
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-muted">
                                  Aucun détail d'article disponible
                                </p>
                              )}
                            </div>

                            {/* Informations de livraison et facturation */}
                            <div className="col-md-4">
                              <h6 className="fw-bold mb-3">
                                <i className="bi bi-info-circle me-2"></i>
                                Informations de commande
                              </h6>

                              {/* Informations de facturation */}
                              {order.billingInfo && (
                                <div className="mb-3">
                                  <h6 className="small fw-bold text-muted mb-2">
                                    FACTURATION
                                  </h6>
                                  <address className="small mb-0">
                                    <strong>
                                      {order.billingInfo.firstName}{" "}
                                      {order.billingInfo.lastName}
                                    </strong>
                                    <br />
                                    {order.billingInfo.email}
                                    <br />
                                    {order.billingInfo.phone && (
                                      <>
                                        {order.billingInfo.phone}
                                        <br />
                                      </>
                                    )}
                                    {order.billingInfo.address && (
                                      <>
                                        {order.billingInfo.address}
                                        <br />
                                        {order.billingInfo.postalCode}{" "}
                                        {order.billingInfo.city}
                                        {order.billingInfo.country && (
                                          <>
                                            <br />
                                            {order.billingInfo.country}
                                          </>
                                        )}
                                      </>
                                    )}
                                  </address>
                                </div>
                              )}

                              {/* Informations de livraison */}
                              {order.shippingInfo &&
                                order.shippingInfo.address && (
                                  <div className="mb-3">
                                    <h6 className="small fw-bold text-muted mb-2">
                                      LIVRAISON
                                    </h6>
                                    <address className="small mb-0">
                                      <strong>
                                        {order.shippingInfo.firstName ||
                                          order.billingInfo?.firstName}{" "}
                                        {order.shippingInfo.lastName ||
                                          order.billingInfo?.lastName}
                                      </strong>
                                      <br />
                                      {order.shippingInfo.address}
                                      <br />
                                      {order.shippingInfo.postalCode}{" "}
                                      {order.shippingInfo.city}
                                      {order.shippingInfo.country && (
                                        <>
                                          <br />
                                          {order.shippingInfo.country}
                                        </>
                                      )}
                                    </address>
                                  </div>
                                )}

                              {/* Informations de suivi */}
                              {order.tracking?.trackingNumber && (
                                <div className="mb-3">
                                  <h6 className="small fw-bold text-muted mb-2">
                                    SUIVI DE LIVRAISON
                                  </h6>
                                  <div className="small">
                                    <p className="mb-1">
                                      <strong>Transporteur:</strong>{" "}
                                      {order.tracking.carrier || "Non spécifié"}
                                    </p>
                                    <p className="mb-2">
                                      <strong>N° de suivi:</strong>{" "}
                                      <code className="text-primary">
                                        {order.tracking.trackingNumber}
                                      </code>
                                    </p>
                                    {order.tracking.trackingUrl && (
                                      <a
                                        href={order.tracking.trackingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-primary"
                                      >
                                        <i className="bi bi-box-arrow-up-right me-1"></i>
                                        Suivre mon colis
                                      </a>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Notes client */}
                              {order.notes?.customer && (
                                <div>
                                  <h6 className="small fw-bold text-muted mb-2">
                                    VOS NOTES
                                  </h6>
                                  <p className="small mb-0 p-2 bg-light rounded">
                                    {order.notes.customer}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="row mt-4 pt-3 border-top">
                            <div className="col-12">
                              <div className="d-flex flex-wrap gap-2 justify-content-end">
                                {order.orderStatus ===
                                  ORDER_STATUS.EN_ATTENTE_PAIEMENT && (
                                  <Link
                                    to={`/checkout/resume?orderId=${order.orderId}`}
                                    className="btn"
                                    style={styles.btnPrimary}
                                  >
                                    <i className="bi bi-credit-card me-2"></i>
                                    Finaliser le paiement
                                  </Link>
                                )}
                                {[
                                  ORDER_STATUS.PAYEE,
                                  ORDER_STATUS.EN_PREPARATION,
                                  ORDER_STATUS.EXPEDIEE,
                                  ORDER_STATUS.LIVREE,
                                ].includes(order.orderStatus) && (
                                  <button
                                    className="btn"
                                    style={styles.btnOutline}
                                    onClick={() =>
                                      handleDownloadInvoice(order.orderId)
                                    }
                                  >
                                    <i className="bi bi-download me-2"></i>
                                    Télécharger la facture
                                  </button>
                                )}
                                {order.orderStatus === ORDER_STATUS.LIVREE && (
                                  <button
                                    className="btn btn-outline-warning"
                                    onClick={() =>
                                      handleLeaveReview(order.orderId)
                                    }
                                  >
                                    <i className="bi bi-star me-2"></i>
                                    Laisser un avis
                                  </button>
                                )}
                                <Link
                                  to="/contact"
                                  className="btn btn-outline-secondary"
                                >
                                  <i className="bi bi-question-circle me-2"></i>
                                  Contacter le support
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA si pas de commandes */}
          {!isLoading && orders.length === 0 && (
            <div className="text-center mt-5 pt-5">
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <h3 className="mb-4">Découvrez nos box surprises !</h3>
                  <p className="text-muted mb-4">
                    Commencez votre expérience BoxPlaisir dès maintenant et
                    recevez votre première box remplie de surprises.
                  </p>
                  <Link
                    to="/category"
                    className="btn btn-lg"
                    style={styles.btnPrimary}
                  >
                    <i className="bi bi-gift me-2"></i>
                    Découvrir nos box
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CSS pour l'animation de rotation */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default Orders;
